import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useScrollReveal from '../hooks/useScrollReveal';
import ProductPopup from './ProductPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Gender theme configs */
const THEMES = {
  men: {
    arabic: 'للرجال',
    label: 'FOR HIM',
    title: 'BEST SELLERS',
    titleAccent: 'FOR HIM',
    accent: '#a89878',       // warm amber/oud
    accentDim: '#6a5a4a',
    accentGlow: 'rgba(168,152,120,0.15)',
    frameBorder: '#3d3428',
    frameInner: '#2a231a',
    frameOuter: 'linear-gradient(135deg, #1a1510 0%, #2a2218 30%, #1f1a14 50%, #2a2218 70%, #1a1510 100%)',
    sealBg: 'linear-gradient(135deg, #2a231a 0%, #3d3428 50%, #2a231a 100%)',
    sealColor: '#8a7a60',
    textPrimary: '#c9b998',
    textSecondary: '#6a5a4a',
    priceTone: '#8a7a60',
    dividerSymbol: '&#9876;',  // sword
  },
  women: {
    arabic: 'للنساء',
    label: 'FOR HER',
    title: 'BEST SELLERS',
    titleAccent: 'FOR HER',
    accent: '#c4a082',       // dusty rose-gold
    accentDim: '#7a5f50',
    accentGlow: 'rgba(196,160,130,0.15)',
    frameBorder: '#4a3535',
    frameInner: '#2e2020',
    frameOuter: 'linear-gradient(135deg, #1a1210 0%, #2a1e1e 30%, #1f1614 50%, #2a1e1e 70%, #1a1210 100%)',
    sealBg: 'linear-gradient(135deg, #2e2020 0%, #4a3535 50%, #2e2020 100%)',
    sealColor: '#a07060',
    textPrimary: '#d4b8a8',
    textSecondary: '#7a5f50',
    priceTone: '#a07060',
    dividerSymbol: '&#10047;', // flower
  },
};

const AncientProductCard = ({ product, theme, hoveredId, setHoveredId, onProductClick }) => {
  const t = THEMES[theme];
  return (
    <div key={product.id}>
      <div
        className="cursor-pointer"
        data-testid={`bestseller-${theme}-${product.id}`}
        onMouseEnter={() => setHoveredId(product.id)}
        onMouseLeave={() => setHoveredId(null)}
        onClick={() => onProductClick(product)}
      >
        <div className="group relative">
          {/* Hover glow */}
          <div className={`absolute -inset-6 transition-opacity duration-500 ${hoveredId === product.id ? 'opacity-100' : 'opacity-0'}`} style={{
            background: `radial-gradient(ellipse at center, ${t.accentGlow} 0%, transparent 60%)`,
            filter: 'blur(15px)'
          }} />

          {/* Ancient weathered frame */}
          <div className="relative p-3" style={{
            background: t.frameOuter,
            boxShadow: 'inset 0 0 30px rgba(0,0,0,0.7), 0 8px 30px rgba(0,0,0,0.5)',
            filter: 'sepia(0.15) saturate(0.85)',
          }}>
            {/* Cracked texture */}
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
              backgroundImage: `
                linear-gradient(45deg, transparent 40%, rgba(60,50,35,0.4) 41%, transparent 42%),
                linear-gradient(-60deg, transparent 55%, rgba(40,35,25,0.3) 56%, transparent 57%)
              `,
            }} />

            {/* Worn borders */}
            <div className="absolute inset-1 pointer-events-none" style={{
              border: `3px solid ${t.frameBorder}`,
              boxShadow: `inset 1px 1px 0 ${t.frameBorder}, inset -1px -1px 0 #151210`,
            }} />
            <div className="absolute inset-2.5 pointer-events-none" style={{ border: `1px solid ${t.frameInner}` }} />

            {/* Corner carvings */}
            {['top-0 left-0', 'top-0 right-0 scale-x-[-1]', 'bottom-0 left-0 scale-y-[-1]', 'bottom-0 right-0 scale-[-1]'].map((pos, i) => (
              <div key={i} className={`absolute ${pos} w-8 h-8 pointer-events-none`}>
                <svg viewBox="0 0 32 32" className="w-full h-full opacity-50">
                  <path d="M0,0 L12,0 L12,4 L4,4 L4,12 L0,12 Z" fill={t.frameBorder} />
                  <path d="M2,2 L10,2 L10,3 L3,3 L3,10 L2,10 Z" fill={t.frameInner} />
                  <circle cx="6" cy="6" r="2" fill={t.frameInner} />
                </svg>
              </div>
            ))}

            {/* Top ornament */}
            <div className="absolute -top-1 left-1/2 -translate-x-1/2 pointer-events-none">
              <svg width="40" height="12" viewBox="0 0 40 12" className="opacity-45">
                <rect x="0" y="5" width="14" height="2" fill={t.frameBorder} rx="1" />
                <polygon points="20,0 26,6 20,12 14,6" fill={t.frameBorder} />
                <polygon points="20,2 24,6 20,10 16,6" fill={t.frameInner} />
                <rect x="26" y="5" width="14" height="2" fill={t.frameBorder} rx="1" />
              </svg>
            </div>

            {/* Bottom ornament */}
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 pointer-events-none">
              <svg width="40" height="12" viewBox="0 0 40 12" className="opacity-45">
                <rect x="0" y="5" width="14" height="2" fill={t.frameBorder} rx="1" />
                <polygon points="20,0 26,6 20,12 14,6" fill={t.frameBorder} />
                <polygon points="20,2 24,6 20,10 16,6" fill={t.frameInner} />
                <rect x="26" y="5" width="14" height="2" fill={t.frameBorder} rx="1" />
              </svg>
            </div>

            {/* Image */}
            <div className="relative aspect-[3/4] overflow-hidden" style={{
              background: '#080604',
              boxShadow: 'inset 0 0 40px rgba(0,0,0,0.9)',
            }}>
              {product.image ? (
                <img src={product.image} alt={product.name} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  style={{ filter: 'sepia(0.1) brightness(0.9)' }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <span className="font-heading text-3xl mb-2" style={{ color: t.frameBorder }}>&#10022;</span>
                  <span className="font-heading text-[10px] tracking-[0.15em]" style={{ color: t.accentDim }}>COMING SOON</span>
                </div>
              )}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'linear-gradient(180deg, transparent 50%, rgba(60,50,35,0.4) 100%)',
              }} />
              <div className="absolute inset-0 pointer-events-none" style={{
                boxShadow: 'inset 0 0 60px rgba(0,0,0,0.7)',
              }} />
              {/* Category seal */}
              <div className="absolute top-3 left-3">
                <div className="px-2 py-1" style={{
                  background: t.sealBg, border: `1px solid ${t.frameBorder}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                }}>
                  <span className="font-heading text-[8px] tracking-wider" style={{ color: t.sealColor }}>
                    {product.brand?.toUpperCase()}
                  </span>
                </div>
              </div>
              {/* Quick view */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="px-5 py-2.5 font-heading text-xs tracking-[0.15em]" style={{
                  background: 'rgba(10,8,4,0.85)', border: `1px solid ${t.frameBorder}`, color: t.accent,
                }}>QUICK VIEW</span>
              </div>
            </div>
          </div>

          {/* Product info */}
          <div className="mt-5 text-center">
            <h3 className="font-heading text-lg tracking-wide transition-colors duration-200" style={{ color: hoveredId === product.id ? t.accent : t.textPrimary }}>
              {product.name}
            </h3>
            <p className="font-body text-sm mt-1" style={{ color: t.textSecondary }}>{product.subtitle}</p>
            <div className="flex items-baseline justify-center gap-2 mt-2">
              {product.original_price && product.original_price > product.price && (
                <span className="font-body text-sm line-through" style={{ color: `${t.textSecondary}80` }}>A${product.original_price.toFixed(2)}</span>
              )}
              <p className="font-heading text-xl" style={{ color: product.discount_pct ? '#D4AF37' : t.priceTone }}>A${product.price?.toFixed(2)}</p>
              {product.discount_pct ? (
                <span className="font-heading text-[9px] tracking-wider px-1.5 py-0.5 bg-[#D4AF37] text-[#050505]">-{product.discount_pct}%</span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FEATURED_MEN = ['blanc-ash', 'la-luna-valley', 'reef-31'];
const FEATURED_WOMEN = ['princess-reef-collection', 'pearl-musk', 'one-and-only'];

const GenderSection = ({ gender, products, onProductClick }) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [sectionRef, isVisible] = useScrollReveal({ threshold: 0.1 });
  const t = THEMES[gender];

  // Show specific featured products
  const featured = gender === 'men' ? FEATURED_MEN : FEATURED_WOMEN;
  const displayProducts = featured
    .map(id => products.find(p => p.id === id))
    .filter(Boolean);

  return (
    <div
      ref={sectionRef}
      className="mb-20 md:mb-28"
      data-testid={`bestsellers-${gender}-section`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      }}
    >
      {/* Sub-header */}
      <div className="text-center mb-12">
        <p className="font-arabic text-xl mb-1" style={{ color: `${t.accent}50` }}>{t.arabic}</p>
        <div className="flex items-center justify-center gap-4 mb-3">
          <div className="w-16 h-px" style={{ background: `linear-gradient(to right, transparent, ${t.frameBorder})` }} />
          <span className="font-heading text-2xl md:text-3xl tracking-[0.15em]" style={{ color: t.accent }}>{t.label}</span>
          <div className="w-16 h-px" style={{ background: `linear-gradient(to left, transparent, ${t.frameBorder})` }} />
        </div>
      </div>

      {/* Products grid */}
      <div className="grid md:grid-cols-3 gap-8 md:gap-10 max-w-5xl mx-auto">
        {displayProducts.map((product) => (
          <AncientProductCard key={product.id} product={product} theme={gender} hoveredId={hoveredId} setHoveredId={setHoveredId} onProductClick={onProductClick} />
        ))}
      </div>
    </div>
  );
};

const FeaturedProducts = () => {
  const [menProducts, setMenProducts] = useState([]);
  const [womenProducts, setWomenProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [headerRef, headerVisible] = useScrollReveal({ threshold: 0.2 });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        const all = res.data;
        setMenProducts(all);
        setWomenProducts(all);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-900/50 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-testid="featured-products-section">
      <div className="absolute inset-0 star-pattern opacity-30" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Main Section Header */}
        <div
          ref={headerRef}
          className="text-center mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
          }}
        >
          <p className="font-arabic text-2xl text-[#8a7a60]/50 mb-2">الأكثر مبيعاً</p>
          <p className="font-heading text-xs tracking-[0.3em] text-[#8a7a60] mb-4">CUSTOMER FAVORITES</p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-wide text-white">
            BEST <span style={{ color: '#a89878' }}>SELLERS</span>
          </h2>
        </div>

        {/* Men's Section */}
        <GenderSection gender="men" products={menProducts} onProductClick={(p) => setPopup({ product: p, gender: 'men' })} />

        {/* Women's Section */}
        <GenderSection gender="women" products={womenProducts} onProductClick={(p) => setPopup({ product: p, gender: 'women' })} />

        {/* View All Link */}
        <div className="text-center">
          <Link
            to="/products?collection=best-sellers"
            className="inline-flex items-center gap-3 px-10 py-4 font-heading text-xs tracking-[0.2em] transition-all duration-300 hover:bg-[#4a3d2e]/30"
            style={{
              border: '2px solid #4a3d2e',
              color: '#a89878',
              background: 'linear-gradient(180deg, rgba(74,61,46,0.1) 0%, transparent 100%)',
            }}
            data-testid="shop-all-btn"
          >
            <span style={{ color: '#6a5a4a' }}>&#10022;</span>
            SHOP ALL FRAGRANCES
            <span style={{ color: '#6a5a4a' }}>&#10022;</span>
          </Link>
        </div>
      </div>

      {/* Product Popup */}
      {popup && <ProductPopup product={popup.product} gender={popup.gender} onClose={() => setPopup(null)} />}
    </section>
  );
};

export default FeaturedProducts;
